import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Textarea } from "@/components/ui/textarea";
import { getEquipment, getCategories, createEquipment, updateEquipment, deleteEquipment, uploadEquipmentImage } from "@/lib/api";
import { toast } from "sonner";
import { Plus, MoreHorizontal, Pencil, Trash2, Search, Camera, X, ImagePlus } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function Equipment() {
  const { t } = useTranslation();
  const { canEdit, canDelete } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [equipment, setEquipment] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [pendingImages, setPendingImages] = useState([]);

  const [formData, setFormData] = useState({
    name: "",
    category_id: "",
    serial_number: "",
    purchase_date: "",
    purchase_price: "",
    currency: "TND",
    invoice_number: "",
    vendor: "",
    condition: t('conditions.new'),
    notes: ""
  });

  const CONDITIONS = [
    t('conditions.new'),
    t('conditions.veryGood'),
    t('conditions.good'),
    t('conditions.acceptable'),
    t('conditions.defective')
  ];

  const CURRENCIES = [
    { code: "TND", name: "Tunesischer Dinar", symbol: "TND" },
    { code: "EUR", name: "Euro", symbol: "€" }
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [equipmentRes, categoriesRes] = await Promise.all([
        getEquipment(),
        getCategories()
      ]);
      setEquipment(equipmentRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      category_id: "",
      serial_number: "",
      purchase_date: "",
      purchase_price: "",
      currency: "TND",
      invoice_number: "",
      vendor: "",
      condition: t('conditions.new'),
      notes: ""
    });
    setEditingItem(null);
    setPendingImages([]);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      if (!file.type.startsWith("image/")) {
        toast.error(t('common.error'));
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(t('common.error'));
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        setPendingImages(prev => [...prev, {
          id: Date.now() + Math.random(),
          data: event.target.result,
          description: ""
        }]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };

  const removeImage = (id) => {
    setPendingImages(prev => prev.filter(img => img.id !== id));
  };

  const updateImageDescription = (id, description) => {
    setPendingImages(prev => prev.map(img => 
      img.id === id ? { ...img, description } : img
    ));
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        name: item.name,
        category_id: item.category_id,
        serial_number: item.serial_number || "",
        purchase_date: item.purchase_date?.split("T")[0] || "",
        purchase_price: item.purchase_price?.toString() || "",
        currency: item.currency || "TND",
        invoice_number: item.invoice_number || "",
        vendor: item.vendor || "",
        condition: item.condition,
        notes: item.notes || ""
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        purchase_price: parseFloat(formData.purchase_price)
      };

      let equipmentId;
      if (editingItem) {
        await updateEquipment(editingItem.id, data);
        equipmentId = editingItem.id;
        toast.success(t('equipment.updated'));
      } else {
        const response = await createEquipment(data);
        equipmentId = response.data.id;
        toast.success(t('equipment.added'));
      }

      if (pendingImages.length > 0 && equipmentId) {
        for (const img of pendingImages) {
          await uploadEquipmentImage(equipmentId, {
            image_data: img.data,
            description: img.description
          });
        }
        toast.success(t('equipment.photosUploaded'));
      }

      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('equipment.confirmDelete'))) return;
    try {
      await deleteEquipment(id);
      toast.success(t('equipment.deleted'));
      loadData();
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "Verfügbar":
        return <span className="badge-success">{t('status.available')}</span>;
      case "Ausgeliehen":
        return <span className="badge-warning">{t('status.loaned')}</span>;
      case "Verkauft":
        return <span className="badge-neutral">{t('status.sold')}</span>;
      default:
        return <span className="badge-neutral">{status}</span>;
    }
  };

  const filteredEquipment = equipment.filter((item) => {
    const matchesSearch = 
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.serial_number.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === "all" || item.category_id === filterCategory;
    const matchesStatus = filterStatus === "all" || item.status === filterStatus;
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString("de-DE");
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (value, currency = "TND") => {
    if (currency === "TND") {
      return `${(value || 0).toFixed(3)} TND`;
    }
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: currency || "EUR"
    }).format(value || 0);
  };

  return (
    <div data-testid="equipment-page">
      <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">{t('equipment.title')}</h1>
          <p className="page-subtitle">{t('equipment.subtitle')}</p>
        </div>
        {canEdit() && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                onClick={() => handleOpenDialog()} 
                className="btn-primary gap-2"
                data-testid="add-equipment-btn"
              >
                <Plus className="w-4 h-4" />
                {t('equipment.add')}
              </Button>
            </DialogTrigger>
          <DialogContent className="sm:max-w-lg" data-testid="equipment-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                {editingItem ? t('equipment.edit') : t('equipment.new')}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label className="form-label">{t('equipment.name')}</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="z.B. Dell Latitude 5520"
                    required
                    data-testid="equipment-name-input"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="form-label">{t('equipment.serialNumber')}</Label>
                  <Input
                    value={formData.serial_number}
                    onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                    placeholder="z.B. SN123456789"
                    data-testid="equipment-serial-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">{t('equipment.serialNumberHint')}</p>
                </div>
                <div>
                  <Label className="form-label">{t('equipment.category')}</Label>
                  <Select
                    value={formData.category_id}
                    onValueChange={(value) => setFormData({ ...formData, category_id: value })}
                    required
                  >
                    <SelectTrigger data-testid="equipment-category-select">
                      <SelectValue placeholder={t('common.selectPlaceholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="form-label">{t('equipment.condition')}</Label>
                  <Select
                    value={formData.condition}
                    onValueChange={(value) => setFormData({ ...formData, condition: value })}
                  >
                    <SelectTrigger data-testid="equipment-condition-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CONDITIONS.map((cond) => (
                        <SelectItem key={cond} value={cond}>
                          {cond}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="form-label">{t('equipment.purchaseDate')}</Label>
                  <Input
                    type="date"
                    value={formData.purchase_date}
                    onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
                    required
                    data-testid="equipment-date-input"
                  />
                </div>
                <div>
                  <Label className="form-label">{t('equipment.purchasePrice')}</Label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      step="0.001"
                      value={formData.purchase_price}
                      onChange={(e) => setFormData({ ...formData, purchase_price: e.target.value })}
                      placeholder="0.000"
                      required
                      className="flex-1"
                      data-testid="equipment-price-input"
                    />
                    <Select
                      value={formData.currency}
                      onValueChange={(value) => setFormData({ ...formData, currency: value })}
                    >
                      <SelectTrigger className="w-28" data-testid="equipment-currency-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CURRENCIES.map((curr) => (
                          <SelectItem key={curr.code} value={curr.code}>
                            {curr.code}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="form-label">{t('equipment.invoiceNumber')}</Label>
                  <Input
                    value={formData.invoice_number}
                    onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                    placeholder="z.B. RE-2024-12345"
                    data-testid="equipment-invoice-input"
                  />
                </div>
                <div>
                  <Label className="form-label">{t('equipment.vendor')}</Label>
                  <Input
                    value={formData.vendor}
                    onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                    placeholder="z.B. Amazon, MediaMarkt"
                    data-testid="equipment-vendor-input"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="form-label">{t('equipment.notes')}</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder={t('common.notesPlaceholder')}
                    rows={3}
                    data-testid="equipment-notes-input"
                  />
                </div>

                <div className="col-span-2 border-t pt-4 mt-2">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="form-label mb-0">{t('equipment.photos')}</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      className="gap-2"
                      data-testid="add-photo-btn"
                    >
                      <ImagePlus className="w-4 h-4" />
                      {t('equipment.addPhoto')}
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      capture="environment"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </div>
                  
                  {pendingImages.length > 0 && (
                    <div className="space-y-3">
                      {pendingImages.map((img) => (
                        <div key={img.id} className="flex gap-3 p-3 bg-slate-50 rounded-lg border">
                          <img
                            src={img.data}
                            alt="Vorschau"
                            className="w-20 h-20 object-cover rounded"
                          />
                          <div className="flex-1">
                            <Input
                              value={img.description}
                              onChange={(e) => updateImageDescription(img.id, e.target.value)}
                              placeholder={t('equipment.photoDescription')}
                              className="mb-2"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeImage(img.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 gap-1"
                            >
                              <X className="w-3 h-3" />
                              {t('equipment.remove')}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {pendingImages.length === 0 && (
                    <p className="text-sm text-slate-500 text-center py-4 bg-slate-50 rounded-lg border border-dashed">
                      {t('equipment.noPhotos')}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                >
                  {t('equipment.cancel')}
                </Button>
                <Button type="submit" className="btn-primary" data-testid="equipment-submit-btn">
                  {editingItem ? t('equipment.save') : t('common.add')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder={t('equipment.search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="equipment-search"
          />
        </div>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-full sm:w-48" data-testid="filter-category">
            <SelectValue placeholder={t('equipment.allCategories')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('equipment.allCategories')}</SelectItem>
            {categories.map((cat) => (
              <SelectItem key={cat.id} value={cat.id}>
                {cat.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-full sm:w-40" data-testid="filter-status">
            <SelectValue placeholder={t('equipment.allStatus')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('equipment.allStatus')}</SelectItem>
            <SelectItem value="Verfügbar">{t('status.available')}</SelectItem>
            <SelectItem value="Ausgeliehen">{t('status.loaned')}</SelectItem>
            <SelectItem value="Verkauft">{t('status.sold')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/50">
              <TableHead className="font-semibold">{t('equipment.serialNumber')}</TableHead>
              <TableHead className="font-semibold">{t('equipment.name')}</TableHead>
              <TableHead className="font-semibold">{t('equipment.category')}</TableHead>
              <TableHead className="font-semibold">{t('equipment.invoiceNumber')}</TableHead>
              <TableHead className="font-semibold">{t('equipment.vendor')}</TableHead>
              <TableHead className="font-semibold">{t('equipment.status')}</TableHead>
              <TableHead className="font-semibold text-right">{t('equipment.price')}</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                  {t('equipment.loading')}
                </TableCell>
              </TableRow>
            ) : filteredEquipment.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                  {t('equipment.noEntries')}
                </TableCell>
              </TableRow>
            ) : (
              filteredEquipment.map((item) => (
                <TableRow key={item.id} className="table-row-hover" data-testid={`equipment-row-${item.id}`}>
                  <TableCell className="font-mono text-sm text-slate-600">
                    {item.serial_number}
                  </TableCell>
                  <TableCell>
                    <button
                      onClick={() => navigate(`/equipment/${item.id}`)}
                      className="font-medium text-primary hover:underline text-left"
                      data-testid={`equipment-link-${item.id}`}
                    >
                      {item.name}
                    </button>
                  </TableCell>
                  <TableCell>{item.category_name}</TableCell>
                  <TableCell className="text-sm">{item.invoice_number || "-"}</TableCell>
                  <TableCell className="text-sm">{item.vendor || "-"}</TableCell>
                  <TableCell>{getStatusBadge(item.status)}</TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(item.purchase_price, item.currency)}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/equipment/${item.id}`)}>
                          <Camera className="w-4 h-4 mr-2" />
                          {t('equipment.photosDetails')}
                        </DropdownMenuItem>
                        {canEdit() && (
                          <DropdownMenuItem onClick={() => handleOpenDialog(item)}>
                            <Pencil className="w-4 h-4 mr-2" />
                            {t('common.edit')}
                          </DropdownMenuItem>
                        )}
                        {canDelete() && (
                          <DropdownMenuItem 
                            onClick={() => handleDelete(item.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            {t('common.delete')}
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
